import { UserAccount } from '../types/userAccount';

/**
 * Owner and Finance Admin are claim-only capabilities. Both inherit ordinary
 * admin access; finance access requires one of these explicit claims.
 * App roles. Mirrors aligned's model: everyone with an account is an expert;
 * `isAdmin` / `isOperationsManager` come from the `user_accounts` doc (the guards'
 * source of truth) and may also be present as custom claims; `isContractorAdmin`
 * is a custom claim only; `isManager` is a custom claim only (computed server-side
 * from the `managers` collection).
 */
export interface AppRoles {
  isOwner?: boolean;
  isFinanceAdmin?: boolean;
  isExpert: boolean;
  isManager: boolean;
  isAdmin: boolean;
  isOperationsManager: boolean;
  isContractorAdmin: boolean;
}

export const NO_ROLES: AppRoles = {
  isOwner: false,
  isFinanceAdmin: false,
  isExpert: false,
  isManager: false,
  isAdmin: false,
  isOperationsManager: false,
  isContractorAdmin: false,
};

export type IdTokenClaims = Record<string, unknown>;

/** Pure role derivation from the account doc + ID-token claims (union for admin/ops). */
export function deriveRoles(
  account: UserAccount | null,
  claims: IdTokenClaims | null | undefined,
): AppRoles {
  if (!account) return NO_ROLES;
  const c = claims ?? {};
  const isOwner = c.isOwner === true;
  const isFinanceAdmin = c.isFinanceAdmin === true;
  return {
    isOwner, isFinanceAdmin,
    isExpert: true,
    isAdmin: isOwner || isFinanceAdmin || account.isAdmin === true || c.isAdmin === true,
    isOperationsManager: account.isOperationsManager === true || c.isOperationsManager === true,
    isContractorAdmin: c.isContractorAdmin === true,
    isManager: c.isManager === true,
  };
}

export type RequiredRole = 'expert' | 'manager' | 'operations' | 'admin' | 'fullAdmin' | 'finance';

/**
 * Guard logic mirroring aligned's AuthGuard/AdminGuard/OperationsGuard.
 *
 * `manager` is deliberately just "has an account" — aligned's real
 * `_handleManagerRedirect` (`auth_guard.dart:53-114`) admits ANY
 * authenticated non-demo user to `/manager*` and auto-provisions a
 * `managers`/`customers` doc pair on first visit (see `ManagerContext`); the
 * `isManager` custom claim exists only for Firestore/Storage security
 * rules, never for app routing. This is not a simplification — it's the
 * actual production gate.
 */
export function hasRouteAccess(required: RequiredRole, roles: AppRoles): boolean {
  switch (required) {
    case 'finance':
      return roles.isOwner === true || roles.isFinanceAdmin === true;
    case 'expert':
      return roles.isExpert;
    case 'admin':
      return roles.isAdmin || roles.isContractorAdmin;
    case 'fullAdmin':
      return roles.isAdmin;
    // Admins may reach operations/manager areas (matches the Dart guards).
    case 'operations':
      return roles.isAdmin || roles.isOperationsManager;
    case 'manager':
      return roles.isExpert;
    default:
      return false;
  }
}
